import { ThreeDot } from "react-loading-indicators";
import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../../../api";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { toast } from "react-toastify";
import { PlusIcon, TrashIcon, Download } from "lucide-react";

const pageLabel = {
	title: "Housekeeping QR Codes",
};
function rangesDoNotOverlap(ranges) {
	if (!Array.isArray(ranges) || ranges.length <= 1) {
		return true;
	}

	const sorted = [...ranges].sort((a, b) => a.rangeStart - b.rangeStart);

	for (let i = 1; i < sorted.length; i++) {
		const prev = sorted[i - 1];
		const curr = sorted[i];
		if (curr.rangeStart <= prev.rangeEnd) {
			return false;
		}
	}
	return true;
}

const HousekeepingQR = () => {
	const [ranges, setRanges] = useState([{ rangeStart: "", rangeEnd: "" }]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [history, setHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [downloadingId, setDownloadingId] = useState(null);
	const { selectedPropertyId } = useProperty();

	const fetchHistory = useCallback(async () => {
		if (!selectedPropertyId) return;
		try {
			setHistoryLoading(true);
			const response = await apiClient.get(`/housekeeping/${selectedPropertyId}/history`);
			setHistory(response.data.result || []);
		} catch (err) {
			console.error("Failed to fetch history:", err);
		} finally {
			setHistoryLoading(false);
		}
	}, [selectedPropertyId]);

	useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);

	const handleDownload = async (id) => {
		try {
			setDownloadingId(id);
			const response = await apiClient.get(
				`/housekeeping/${selectedPropertyId}/history/${id}/download`,
				{ responseType: "blob" }
			);
			
			const blob = new Blob([response.data], { type: "application/pdf" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `housekeeping_qr_codes_${id}.pdf`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			
			toast.success("PDF downloaded successfully!");
		} catch (err) {
			console.error("Failed to download PDF:", err);
			toast.error("Failed to download PDF. Please try again.");
		} finally {
			setDownloadingId(null);
		}
	};

	const handleGenerate = async () => {
		for (const range of ranges) {
			const start = parseInt(range.rangeStart, 10);
			const end = parseInt(range.rangeEnd, 10);
			console.log(start, end);
			if (isNaN(start) || isNaN(end)) {
				setError("Please enter valid room numbers.");
				return;
			}
			if (start > end) {
				setError(
					"Start room number must be less than or equal to end room number.",
				);
				return;
			}
			if (start <= 0 || end <= 0) {
				setError("Room numbers must be positive integers.");
				return;
			}
		}
		if (!rangesDoNotOverlap(ranges)) {
			setError("Room ranges must not overlap.");
			return;
		}

		setError("");

		try {
			setLoading(true);
			await apiClient.post(
				`/housekeeping/${selectedPropertyId}/sendEmail`,
				{ ranges: ranges },
			);
			toast.success(
				"QR codes are being generated and will be sent to your email shortly.",
			);
			fetchHistory();
		} catch (err) {
			setError(
				"An error occurred while generating QR codes. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};
	const onRangeStartChange = (value, index) => {
		const updatedRanges = [...ranges];
		updatedRanges[index].rangeStart = value;
		setRanges(updatedRanges);
	};
	const onRangeEndChange = (value, index) => {
		const updatedRanges = [...ranges];
		updatedRanges[index].rangeEnd = value;
		setRanges(updatedRanges);
	};

	return (
		<div className="bg-gray-100 min-h-screen p-12 flex justify-center">
			<div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 w-full">
				{/* Header */}
				<h2 className="text-2xl font-semibold text-gray-900 mb-6">
					{pageLabel.title}
				</h2>

				{/* Range Inputs */}
				{ranges.map((range, index) => (
					<div className="flex gap-6 mb-4 items-center" key={index}>
						<div>
							<label
								htmlFor="rangeStart"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Start Room
							</label>
							<input
								name="rangeStart"
								type="number"
								placeholder="e.g. 101"
								value={range.rangeStart}
								onChange={(e) => onRangeStartChange(e.target.value, index)}
								className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>

						<div>
							<label
								htmlFor="rangeEnd"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								End Room
							</label>
							<input
								name="rangeEnd"
								type="number"
								value={range.rangeEnd}
								onChange={(e) => onRangeEndChange(e.target.value, index)}
								placeholder="e.g. 120"
								className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>
						<div>
							{ranges.length > 1 && (
								<button
									onClick={() => {
										const updatedRanges = ranges.filter((_, i) => i !== index);
										setRanges(updatedRanges);
									}}
									className="mt-6 text-red-600 hover:text-red-800 p-2"
								>
									<TrashIcon className="h-5 w-5" />
								</button>
							)}
						</div>
					</div>
				))}
				{/* Error */}
				{error && <p className="text-sm text-red-600 mb-4">{error}</p>}

				{/* Add Range Button */}
				<div className="flex justify-start mb-4">
					<button
						onClick={() =>
							setRanges([...ranges, { rangeStart: "", rangeEnd: "" }])
						}
						className="px-4 py-2 rounded-md bg-green-600 text-white font-medium hover:bg-green-700 flex items-center"
					>
						{" "}
						<PlusIcon className="h-5 w-5 inline-block mr-2" />
						Add Rooms
					</button>
				</div>

				{/* Action */}
				<div className="flex justify-start">
					<button
						onClick={handleGenerate}
						disabled={loading}
						className="px-6 py-2 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
					>
						Send QR Codes via Email
					</button>
				</div>

				{/* Loader */}
				{loading && (
					<div className="flex justify-center mt-6">
						<ThreeDot
							width={50}
							height={50}
							color="#4F46E5"
							variant="brick-stack"
						/>
					</div>
				)}

				{/* Divider */}
				<hr className="my-8 border-gray-200" />

				{/* History Section */}
				<div>
					<h3 className="text-lg font-semibold text-gray-900 mb-4">
						History of Sent QR Codes
					</h3>

					{historyLoading ? (
						<div className="flex justify-center py-8">
							<ThreeDot
								width={40}
								height={40}
								color="#4F46E5"
								variant="brick-stack"
							/>
						</div>
					) : history.length === 0 ? (
						<div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50">
							<p className="text-gray-500 text-sm">
								No history of sent QR codes found.
							</p>
						</div>
					) : (
						<div className="overflow-x-auto border border-gray-200 rounded-lg">
							<table className="min-w-full divide-y divide-gray-200 text-sm text-left">
								<thead className="bg-gray-50 text-gray-700 uppercase text-xs tracking-wider">
									<tr>
										<th className="px-6 py-3 font-semibold">
											Date & Time
										</th>
										<th className="px-6 py-3 font-semibold">
											Sent To (Email)
										</th>
										<th className="px-6 py-3 font-semibold">
											Room Ranges
										</th>
										<th className="px-6 py-3 font-semibold">
											Status
										</th>
										<th className="px-6 py-3 font-semibold">
											Action
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200 text-gray-600">
									{history.map((item) => (
										<tr key={item._id} className="hover:bg-gray-50 transition-colors">
											<td className="px-6 py-4 whitespace-nowrap text-gray-900">
												{new Date(item.createdAt).toLocaleString(undefined, {
													year: "numeric",
													month: "short",
													day: "numeric",
													hour: "2-digit",
													minute: "2-digit",
												})}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{item.email}
											</td>
											<td className="px-6 py-4">
												{item.ranges
													.map((r) => `${r.rangeStart} - ${r.rangeEnd}`)
													.join(", ")}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
													{item.status || "Sent"}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<button
													onClick={() => handleDownload(item._id)}
													disabled={downloadingId === item._id}
													className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
												>
													{downloadingId === item._id ? (
														<>
															<span className="animate-spin h-3.5 w-3.5 border-2 border-indigo-600 border-t-transparent rounded-full mr-1"></span>
															Downloading...
														</>
													) : (
														<>
															<Download className="h-3.5 w-3.5" />
															Download PDF
														</>
													)}
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default HousekeepingQR;
